import { coverSectionTitle } from '@/utils/programCoverSections';
import './program-public-theme.css';
import { programThemeStyle } from '@/utils/programThemes';
import PincodeField from '@/components/PincodeField';
import { isLocationQuestion, isResolvedPostalAnswer } from '@/utils/postalQuestions';
import React, { useEffect, useState } from 'react';
import { fileUrl } from '@/api';
import { toast } from 'sonner';
import { RichText, stripHtml } from '@/components/RichTextEditor';
import { splitIntoPages, nextDestination, reachableQuestionIds, isAnswerEmpty, GO_SUBMIT } from '@/utils/programForm';
import { Buildings, Clock, CheckCircle, CaretLeft, CaretRight, ArrowRight } from '@phosphor-icons/react';
import ChoiceField from '@/components/ChoiceField';

// ---------------------------------------------------------------------------
// Shared between the public apply page (PublicApply.jsx) and the form builder's
// Preview (VCPrograms.jsx) so both render the exact same left-carousel/right-form
// split layout and question/branching logic — no separate "editor preview" copy.
// ---------------------------------------------------------------------------

export const isUploadType = (t) => t === 'pitch_deck' || t === 'financial_statement' || t === 'file' || t === 'image';

const PostalAnswer = ({q,answers,setAnswers}) => {
  const value=typeof answers[q.id]==='string'?answers[q.id]:'';
  const pin=value.match(/^(\d{1,6})(?: — |$)/)?.[1]||'';
  return <PincodeField label="Pincode" hideLabel required={q.required} value={pin} onChange={value=>setAnswers(current=>({...current,[q.id]:value}))} onResolve={location=>setAnswers(current=>({...current,[q.id]:`${location.pincode} — ${location.label}`}))} testId={`answer-${q.id}`}/>;
};

export const QuestionField = ({ q, answers, setAnswers, fileUploads, uploadingQid, onUpload }) => {
  const accept = q.type === 'pitch_deck'
    ? '.pdf,.pptx,.ppt'
    : q.type === 'financial_statement'
      ? '.pdf,.xlsx,.xls,.csv'
      : q.type === 'image'
        ? '.png,.jpg,.jpeg,.webp'
        : '.pdf,.pptx,.ppt,.xlsx,.xls,.csv';
  if (isLocationQuestion(q)) return <PostalAnswer q={q} answers={answers} setAnswers={setAnswers}/>;
  // 'text_box' is a legacy alias — same single-line input, now sharing 'short_answer's cap.
  if (q.type === 'short_answer' || q.type === 'text_box') {
    const cap = 50;
    const val = answers[q.id] || '';
    return (
      <div>
        <input
          type="text"
          value={val}
          maxLength={cap}
          onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
          placeholder={q.placeholder || 'Your answer...'}
          data-testid={`q-input-${q.id}`}
          className="w-full bg-white border border-border rounded-lg px-3 py-2.5 text-base md:text-sm text-text-primary focus:outline-none focus:border-primary"
        />
        <div className="text-[10px] text-text-muted text-right mt-0.5 tabular-nums">{val.length}/{cap}</div>
      </div>
    );
  }
  // 'long_answer' and legacy 'text' are aliases for 'paragraph'.
  if (q.type === 'paragraph' || q.type === 'long_answer' || q.type === 'text') {
    const val = answers[q.id] || '';
    const cap = 1000;
    return (
      <div>
        <textarea
          value={val}
          maxLength={cap}
          onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
          rows={4}
          placeholder={q.placeholder || 'Your answer...'}
          data-testid={`q-input-${q.id}`}
          className="w-full bg-white border border-border rounded-lg px-3 py-2.5 text-base md:text-sm text-text-primary focus:outline-none focus:border-primary"
        />
        {cap && <div className="text-[10px] text-text-muted text-right mt-0.5 tabular-nums">{val.length}/{cap}</div>}
      </div>
    );
  }
  if (q.type === 'single_choice' || q.type === 'multi_choice') {
    return (
      <ChoiceField
        q={q}
        value={answers[q.id] ?? (q.type === 'multi_choice' ? [] : '')}
        onChange={(v) => setAnswers({ ...answers, [q.id]: v })}
        testId={`q-input-${q.id}`}
      />
    );
  }
  if (q.type === 'dropdown') {
    return (
      <select
        value={answers[q.id] || ''}
        onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
        data-testid={`q-input-${q.id}`}
        className="w-full bg-white border border-border rounded-lg px-3 py-2.5 text-base md:text-sm text-text-primary focus:outline-none focus:border-primary"
      >
        <option value="">Select…</option>
        {(q.options || []).map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    );
  }
  if (q.type === 'pitch_deck' || q.type === 'financial_statement' || q.type === 'file' || q.type === 'image') {
    const uploaded = fileUploads[q.id];
    return (
      <div className="space-y-2" data-testid={`q-input-${q.id}`}>
        <label
          className="flex items-center justify-between gap-2 px-3 py-2.5 bg-white border border-dashed border-border rounded-lg hover:border-primary cursor-pointer transition-colors"
          data-testid={`upload-btn-${q.id}`}
        >
          <span className="text-sm text-text-secondary flex-1 truncate">
            {uploaded?.filename
              ? <><CheckCircle size={12} weight="fill" className="inline text-success mr-1" /> {uploaded.filename}</>
              : uploadingQid === q.id
                ? 'Uploading…'
                : `Click to upload ${q.type === 'pitch_deck' ? 'pitch deck' : q.type === 'financial_statement' ? 'financial statement' : q.type === 'image' ? 'image' : 'file'} (${accept.replaceAll('.', '').replaceAll(',', '/')})`
            }
          </span>
          <span className="text-[11px] uppercase tracking-wider text-primary font-bold">{uploaded ? 'Replace' : 'Choose'}</span>
          <input
            type="file"
            accept={accept}
            onChange={(e) => onUpload(q.id, e.target.files?.[0])}
            className="hidden"
          />
        </label>
        <div className="text-[10px] text-text-muted">Max 25 MB.</div>
      </div>
    );
  }
  return null;
};

// One section per step on every device. Next/Back retain answers; required
// validation checks all branch-reachable questions on the final submit.
export const SectionedForm = ({ questions, answers, setAnswers, fileUploads, uploadingQid, onUpload, onSubmit, submitting, submitLabel, submitTestId, accentBtn, footerExtra, footerHint, leadingFields, resumeKey }) => {
  const pages = splitIntoPages(questions);
  const [pageIdx, setPageIdx] = useState(0);
  const [history, setHistory] = useState([]);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  // Callers may recreate the question array while an applicant types. Reset only
  // when the ordered question/section structure changes, not on array identity.
  const structureKey = JSON.stringify(questions.map(q => [q.id, q.type]));
  useEffect(() => { setPageIdx(0); setHistory([]); setAttemptedSubmit(false); }, [structureKey]);

  const isAnswered = (q) => isLocationQuestion(q)?isResolvedPostalAnswer(answers[q.id]):(isUploadType(q.type) ? !!fileUploads[q.id]?.file_id : !isAnswerEmpty(answers[q.id]));
  const isMissing = (q) => (q.required || (isLocationQuestion(q)&&!!answers[q.id])) && !isAnswered(q);

  // (Iter 58) A restored draft should land on the section the applicant actually stopped
  // at, not back at page 1. Walk forward over pages they already filled — following the
  // branch their own answers select — and stop at the first one they never started.
  useEffect(() => {
    if (!resumeKey) return;
    const trail = [];
    let cur = 0;
    for (let guard = 0; guard < pages.length; guard++) {
      const items = pages[cur]?.items || [];
      const startedHere = items.some(isAnswered);
      const requiredDone = items.every((q) => !q.required || isAnswered(q));
      if (!startedHere || !requiredDone) break;
      const dest = nextDestination(pages, cur, answers);
      if (dest === GO_SUBMIT) break;
      trail.push(cur);
      cur = dest;
    }
    setHistory(trail);
    setPageIdx(cur);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeKey]);

  const idx = Math.min(pageIdx, pages.length - 1);
  const screenDest = nextDestination(pages, idx, answers);
  const screenPages = [pages[idx]];
  const isLast = screenDest === GO_SUBMIT;
  const showProgress = pages.length > 1 || pages.some(page => page.section);
  const reachable = reachableQuestionIds(questions, answers);
  const progressQuestions = questions.filter(q => q.type !== 'section' && reachable.has(q.id));
  const answeredCount = progressQuestions.filter(isAnswered).length;
  const progress = progressQuestions.length ? Math.round((answeredCount / progressQuestions.length) * 100) : 0;

  // Full-form check across every branch-reachable question (not just this page) —
  // run once, on the final Submit click. Returns the missing questions in form order.
  const findMissing = () => {
    const reachable = reachableQuestionIds(questions, answers);
    return questions.filter((q) => reachable.has(q.id) && isMissing(q));
  };
  const goNext = () => {
    if (!isLast) {
      setHistory((h) => [...h, idx]);
      setPageIdx(screenDest);
      return;
    }
    const missing = findMissing();
    if (missing.length) {
      setAttemptedSubmit(true);
      toast.error(missing.length === 1
        ? `Please answer: ${stripHtml(missing[0].label)}`
        : `${missing.length} required questions are still missing — they're highlighted below.`);
      const missingIds = new Set(missing.map((q) => q.id));
      const firstPage = pages.findIndex((p) => p.items.some((q) => missingIds.has(q.id)));
      if (firstPage >= 0 && firstPage !== idx) {
        setHistory((h) => [...h, idx]);
        setPageIdx(firstPage);
      }
      return;
    }
    onSubmit();
  };
  const goBack = () => setHistory((h) => { const c = [...h]; const prev = c.pop(); setPageIdx(prev ?? 0); return c; });

  const accentFill = (accentBtn || '').split(' ')[0] || 'bg-primary';

  return (
    <div className="space-y-4">
      {showProgress && (
        <div data-testid="form-progress">
          <div className="flex items-center justify-between text-[11px] text-text-muted mb-1.5">
            <span className="font-medium tabular-nums">Section {idx + 1} of {pages.length}</span>
            <span className="tabular-nums">{progress}% complete</span>
          </div>
          <div className="h-1.5 bg-border rounded-full overflow-hidden" role="progressbar" aria-label="Application completion" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}>
            <div className={`h-full rounded-full transition-all ${accentFill}`} style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}
      {idx === 0 && leadingFields}
      {screenPages.map((page, pageOffset) => (
        <div key={page.key} className={pageOffset > 0 ? 'pb-section-step pt-5 mt-1 border-t border-border space-y-4' : 'pb-section-step space-y-4'}>
          {page.section && (
            <div className="pb-2 border-b-2 border-primary/30">
              <div className="text-lg font-heading font-bold text-text-primary"><RichText html={page.section.label} className="[&_*]:inline" /></div>
            </div>
          )}
          {page.items.map((q) => {
            const missing = attemptedSubmit && isMissing(q);
            return (
              <div key={q.id} data-testid={`q-${q.id}`}
                className={missing ? 'rounded-lg ring-1 ring-danger/50 bg-danger/5 p-3' : ''}>
                <div className="flex items-baseline gap-1 text-sm text-text-primary font-medium mb-1.5">
                  <RichText html={isLocationQuestion(q)?'Pincode':q.label} className="[&_*]:inline" />
                  {q.required && <span className="text-danger font-bold">*</span>}
                </div>
                <QuestionField q={q} answers={answers} setAnswers={setAnswers} fileUploads={fileUploads} uploadingQid={uploadingQid} onUpload={onUpload} />
                {missing && <div className="text-[11px] text-danger font-medium mt-1" data-testid={`q-missing-${q.id}`}>This field is required.</div>}
              </div>
            );
          })}
        </div>
      ))}
      <div className="border-t border-border pt-4">
        <div className="flex items-center justify-end gap-3 flex-wrap">
          {history.length > 0 && (
            <button type="button" onClick={goBack} className="px-4 py-2.5 text-sm text-text-secondary hover:text-text-primary border border-border rounded-lg inline-flex items-center gap-1" data-testid="form-previous-btn">
              <CaretLeft size={12} weight="bold" /> Previous section
            </button>
          )}
          {footerExtra}
          <button type="button" onClick={goNext} disabled={submitting}
            className={`px-6 py-2.5 ${accentBtn} text-white rounded-lg text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2`}
            data-testid={isLast ? (submitTestId || 'form-submit-btn') : 'form-next-btn'}>
            {isLast ? (submitting ? 'Submitting...' : submitLabel) : <>Next <CaretRight size={12} weight="bold" /></>}
          </button>
        </div>
        {footerHint && <div className="text-[11px] text-text-muted text-right mt-2">{footerHint}</div>}
      </div>
    </div>
  );
};

// Whether the watermark needs light or dark text to stay readable, computed
// deterministically instead of sampled from pixels (a cross-origin canvas read of
// an uploaded photo is unreliable — it can be blocked outright by the file host's
// CORS headers, and even when it succeeds it reads the *raw* photo, ignoring the
// black gradient scrim already painted over the bottom of it — see the "from-black/85"
// overlay below, which is exactly where the watermark sits. That scrim keeps the
// composited background dark there regardless of the source photo, so light text is
// always correct when a cover photo is set.
//
// The only case that can genuinely go either way is the no-photo fallback gradient
// (bg-gradient-to-br from-accent/90 via-accent/60 to-slate-900), so that's computed
// for real from the theme's actual accent hex (tailwind.config.js) composited toward
// slate-900 at roughly the watermark's position along that diagonal.
const hexToRgb = (hex) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : [0, 0, 0];
};
const relativeLuminance = ([r, g, b]) => 0.299 * r + 0.587 * g + 0.114 * b;
const mixRgb = (a, b, t) => [0, 1, 2].map((i) => Math.round(a[i] + (b[i] - a[i]) * t));
const ACCENT_HEX = { warning: '#f59e0b', primary: '#6c63ff' };
const SLATE_900_RGB = hexToRgb('#0f172a');

function watermarkNeedsLightText(isIncubator, hasPhoto) {
  if (hasPhoto) return true;
  const accent = hexToRgb(ACCENT_HEX[isIncubator ? 'warning' : 'primary']);
  const composited = mixRgb(accent, SLATE_900_RGB, 0.55); // bottom-center, past the 'via' stop
  return relativeLuminance(composited) < 150;
}

// Left-hand hero/carousel panel — program branding over a rotating set of
// VC-uploaded cover images (falls back to a plain accent gradient with none).
export const CoverCarouselPanel = ({ program, isIncubator, coverEditor }) => {
  const images = program?.cover_images || [];
  const total = images.length;
  const [idx, setIdx] = useState(0);
  const watermarkLight = watermarkNeedsLightText(isIncubator, total > 0);

  const coverQuestionCount = program.questions?.filter(q => q.type !== 'section' && stripHtml(q.label)).length || 0;
  const imageKey = images.join('|');
  useEffect(() => { setIdx(0); }, [imageKey]);
  useEffect(() => {
    if (total < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % total), 5000);
    return () => clearInterval(t);
  }, [total]);

  return (
    <div className={`relative h-full min-h-[320px] lg:min-h-full overflow-hidden bg-slate-900 ${isIncubator ? 'program-cover-editorial' : ''} ${total === 1 && images[0] === 'builtin:charcoal' ? 'program-cover-charcoal' : ''}`} data-testid="cover-carousel-panel">
      {/* Background: rotating cover images, or a plain accent gradient when none are set */}
      {total > 0 ? (
        <>
          {images.map((img, i) => (
            <img
              key={img + i}
              src={fileUrl(img)}
              alt=""
              className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
              style={{ opacity: i === idx ? 1 : 0 }}
              data-testid={`cover-image-${i}`}
            />
          ))}
          <div className="program-cover-scrim absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/10" />
        </>
      ) : (
        <div className={`program-cover-fallback absolute inset-0 bg-gradient-to-br ${isIncubator ? 'from-warning/90 via-warning/60 to-slate-900' : 'from-primary/90 via-primary/60 to-slate-900'}`}>
          <div className="absolute -left-16 -top-16 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-16 right-0 h-64 w-64 rounded-full bg-black/20 blur-3xl" />
        </div>
      )}

      {/* Foreground content */}
      <div className="program-cover-content relative z-10 h-full flex flex-col p-8 text-white">
        <div className="program-cover-masthead"><div className="program-cover-org flex items-center gap-3">
          {program.fund_logo_url ? (
            <img src={fileUrl(program.fund_logo_url)} className="w-11 h-11 rounded-lg object-cover bg-white/10" alt={program.fund_name} />
          ) : (
            <div className="w-11 h-11 rounded-lg bg-white/15 flex items-center justify-center">
              <Buildings size={22} weight="bold" />
            </div>
          )}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/60">{isIncubator ? 'Incubator Program' : 'Funding Watchlist'}</div>
            <div className="font-heading font-bold text-sm">{program.fund_name || 'Founderboard'}</div>
          </div>
        </div>

        {isIncubator && program.status === 'open' && <span className="program-cover-status"><i/>Applications open</span>}
        </div>
        <div className="program-cover-story flex-1 flex flex-col justify-center max-w-md py-6">
          {isIncubator && <span className="program-cover-rule"/>}
          {coverEditor || <>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold leading-tight mb-2">{isIncubator ? program.name : (program.hero_text || `Apply to ${program.name}`)}</h1>
          {isIncubator && program.hero_text && <h2 className="program-cover-subheading">{program.hero_text}</h2>}
          {program.hero_text && !isIncubator && <h2 className="text-sm text-white/70 mb-2">{program.name}</h2>}
          {program.description && (
            <RichText html={program.description} className="program-cover-description text-sm text-white/80 leading-relaxed [&_*]:text-white/80" />
          )}
          {isIncubator && coverQuestionCount > 0 && <div className="program-cover-meta"><span>{coverQuestionCount} question{coverQuestionCount === 1 ? '' : 's'}</span></div>}
          {isIncubator && <div className="program-cover-sections">{(program.cover_sections || []).filter(section => section.enabled !== false && section.body?.trim()).map((section,index) => <section key={`${section.kind}-${index}`} data-testid={`public-cover-section-${section.kind}`}><h3>{coverSectionTitle(section)}</h3><p>{section.body}</p></section>)}</div>}
          </>}
          {program.status === 'open' && program.open_until && (
            <div className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-medium bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-white w-fit" data-testid="apply-deadline">
              <Clock size={13} weight="bold" /> Applications close on {new Date(program.open_until).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
            </div>
          )}
        </div>

        {total > 1 && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5" data-testid="cover-dots">
              {images.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  data-testid={`cover-dot-${i}`}
                  aria-label={`Go to image ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-6 bg-white' : 'w-1.5 bg-white/35'}`}
                />
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setIdx((i) => (i - 1 + total) % total)}
                data-testid="cover-prev-btn"
                aria-label="Previous"
                className="w-7 h-7 flex items-center justify-center rounded-full border border-white/25 text-white/80 hover:text-white hover:border-white/50 transition-colors"
              >
                <CaretLeft size={12} weight="bold" />
              </button>
              <button
                onClick={() => setIdx((i) => (i + 1) % total)}
                data-testid="cover-next-btn"
                aria-label="Next"
                className="w-7 h-7 flex items-center justify-center rounded-full border border-white/25 text-white/80 hover:text-white hover:border-white/50 transition-colors"
              >
                <CaretRight size={12} weight="bold" />
              </button>
            </div>
          </div>
        )}

        <div className={`program-cover-footer flex items-center justify-center ${total > 1 ? 'pt-3' : 'pt-2'}`}>
          <div className="inline-flex items-center gap-2" data-testid="cover-watermark">
            <img src="/founderboard-logo.png" alt="" className="w-8 h-8 rounded object-contain" />
            <span className={`text-[11px] font-semibold uppercase tracking-[0.12em] ${watermarkLight ? 'text-white/90' : 'text-slate-800/90'}`}>
              Powered by FounderBoard
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Two-column shell: cover carousel on the left, form content on the right.
// Stacks to a single column below the lg breakpoint.
// Below the lg breakpoint this is a two-step flow, not a stack: the cover panel is
// the whole screen first (branding, no form fields fighting it for space), then
// "Continue to application" swaps it out for a clean, full-width, carousel-free
// form. At lg+ both panels show side by side as usual, sized up to ~75% of the
// viewport so the split makes real use of large screens instead of staying capped
// at a fixed width.
export const ApplySplitShell = ({
  leftPanel, children, isIncubator, topRightAction = null, initialMobileStep = 'cover',
  widthClass = 'lg:w-[86.25vw] lg:max-w-[1656px]',
  heightClass = 'lg:h-[86.25vh]',
  frameClass = 'rounded-none sm:rounded-2xl sm:overflow-hidden border-0 sm:border sm:border-border sm:shadow-sm',
}) => {
  const [mobileStep, setMobileStep] = useState(initialMobileStep); // 'cover' | 'form' — mobile/tablet only
  const accentBg = isIncubator ? 'bg-warning hover:bg-warning/90' : 'bg-primary hover:bg-primary/90';

  return (
    <div className={`mx-auto w-full ${widthClass} ${heightClass} ${frameClass} bg-surface grid grid-cols-1 lg:grid-cols-[2fr_3fr]`} data-testid="apply-split-shell">
      {mobileStep === 'cover' ? (
        <div className="flex flex-col min-h-[100dvh] lg:contents">
          <div className="relative flex-1 lg:min-h-full lg:h-full">{leftPanel}</div>
          <button
            type="button"
            onClick={() => setMobileStep('form')}
            data-testid="mobile-continue-to-form-btn"
            className={`lg:hidden sticky bottom-0 z-20 shrink-0 w-full ${accentBg} text-white shadow-[0_-6px_20px_rgba(0,0,0,0.3)] border-t border-white/15`}
            style={{ paddingTop: '1rem', paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))', paddingLeft: '1.5rem', paddingRight: '1.5rem' }}
          >
            <span className="flex items-center justify-center gap-2 text-base font-semibold">
              Continue to application <ArrowRight size={17} weight="bold" />
            </span>
          </button>
        </div>
      ) : (
        <div className="relative hidden lg:block lg:min-h-full lg:h-full">{leftPanel}</div>
      )}
      <div className={`program-form-surface p-6 sm:p-8 lg:h-full lg:overflow-y-auto ${mobileStep === 'form' ? '' : 'hidden lg:block'}`}>
        {topRightAction && (
          <div className="mb-4 flex justify-end">{topRightAction}</div>
        )}
        {children}
      </div>
    </div>
  );
};

// The public route and builder preview share viewport geometry and theme scope.
export const ProgramPublicFrame = ({ program, isIncubator, children, topRightAction, initialMobileStep, coverEditor }) => (
  <div data-program-theme={program.theme || undefined} style={programThemeStyle(program.theme)} className="program-public-page relative min-h-screen bg-background py-0 sm:py-12 px-0 sm:px-4 lg:flex lg:h-screen lg:items-center lg:justify-center lg:overflow-hidden lg:py-0 lg:px-0">
    <ApplySplitShell leftPanel={<CoverCarouselPanel program={program} isIncubator={isIncubator} coverEditor={coverEditor} />} isIncubator={isIncubator} widthClass="lg:w-full" heightClass="lg:h-screen" frameClass="rounded-none border-0 shadow-none" topRightAction={topRightAction} initialMobileStep={initialMobileStep}>{children}</ApplySplitShell>
  </div>
);

export const ProgramApplyPrompt = ({ program, isIncubator, onApply }) => (
  <div className="text-center" data-testid="apply-prompt">
    <div className={`w-14 h-14 mx-auto ${isIncubator ? 'bg-warning/10' : 'bg-primary/10'} rounded-full flex items-center justify-center mb-4`}><Buildings size={26} weight="fill" className={isIncubator ? 'text-warning' : 'text-primary'} /></div>
    <h3 className="font-heading font-bold text-xl text-text-primary mb-1.5">Ready to apply to {program.fund_name}?</h3>
    <p className="text-sm text-text-secondary mb-5 max-w-md mx-auto">Apply with your Founderboard profile for a stronger application, or use a quick basic form.</p>
    <button type="button" onClick={onApply} data-testid="open-apply-choice-btn" className={`px-6 py-3 ${isIncubator ? 'bg-warning hover:bg-warning/90' : 'bg-primary hover:bg-primary/90'} text-white rounded-xl font-semibold inline-flex items-center gap-2`}>Apply now <ArrowRight size={16} weight="bold" /></button>
  </div>
);
